
import React, { useState, useEffect, useMemo } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { Event, SubscriptionTier } from '../types.ts';

const EventsView = ({ user, onLogout, onChangeTab, activeTab = 'events' }: any) => {
  const [profile] = useState(dbService.getStudentProfile(user.id));
  const [events, setEvents] = useState<Event[]>(dbService.getEvents());
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<'network' | 'campus' | 'local' | 'global'>('network');

  const userTier = user.tier || SubscriptionTier.BASIC;
  const isLocked = userTier === SubscriptionTier.BASIC;

  useEffect(() => {
    if (!isLocked && events.length === 0) fetchEvents();
  }, [profile?.major]);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const results = await aiService.getEvents(profile);
      const stabilized = results.map((ev, i) => ({
        ...ev,
        id: ev.id || `evt-${i}`
      }));
      setEvents(stabilized);
      dbService.saveEvents(stabilized);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const filteredEvents = useMemo(() => {
    if (activeCategory === 'campus') {
        const schoolKeywords = [profile?.school, profile?.campus].filter(Boolean).map(s => s!.toLowerCase());
        return events.filter(e => 
            schoolKeywords.some(k => 
                e.location.toLowerCase().includes(k) || 
                e.title.toLowerCase().includes(k) || 
                e.organizer.toLowerCase().includes(k)
            )
        );
    } else if (activeCategory === 'local') {
        const city = profile?.city?.toLowerCase() || '';
        return events.filter(e => e.location.toLowerCase().includes(city) && !e.location.toLowerCase().includes(profile?.school?.toLowerCase() || '#####'));
    } else if (activeCategory === 'global') {
        const city = profile?.city?.toLowerCase() || '';
        return events.filter(e => !e.location.toLowerCase().includes(city) && !e.location.toLowerCase().includes(profile?.school?.toLowerCase() || '#####'));
    }
    return events;
  }, [events, activeCategory, profile]);

  return (
    <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-y-auto">
        <header className="mb-12 flex flex-col md:flex-row justify-between items-end gap-6">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-600 dark:text-indigo-400 mb-2 block">Industry Networking</span>
            <h1 className="text-5xl font-black text-blue-950 dark:text-white tracking-tighter">Event Hub.</h1>
          </div>
          {!isLocked && (
             <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide bg-white dark:bg-slate-900 p-1.5 rounded-[2.2rem] border border-slate-100 dark:border-slate-800 shadow-xl max-w-full">
                <button 
                   onClick={() => setActiveCategory('network')}
                   className={`shrink-0 px-6 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === 'network' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
                >
                   All Events
                </button>
                <button 
                   onClick={() => setActiveCategory('campus')}
                   className={`shrink-0 px-6 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === 'campus' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
                >
                   {profile?.campus || 'Campus'} Hub
                </button>
                <button 
                   onClick={() => setActiveCategory('local')}
                   className={`shrink-0 px-6 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === 'local' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
                >
                   {profile?.city || 'Local'} Mixers
                </button>
                <button 
                   onClick={() => setActiveCategory('global')}
                   className={`shrink-0 px-6 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === 'global' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
                >
                   Global / Virtual
                </button>
             </div>
          )}
        </header>

        {isLocked ? (
          <div className="bg-white dark:bg-slate-900 p-20 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-4xl text-center flex flex-col items-center animate-in zoom-in-95 duration-500">
             <div className="w-24 h-24 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-[2.5rem] flex items-center justify-center text-4xl mb-10 shadow-inner">
                <i className="fas fa-lock"></i>
             </div>
             <h2 className="text-4xl font-black text-blue-950 dark:text-white tracking-tighter mb-4">Event Radar Locked.</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-md mb-12 leading-relaxed">
               Access real-time networking mixers and recruiting events tailored to your discipline. Upgrade to <span className="text-blue-600 dark:text-indigo-400 font-black">Standard</span> to unlock.
             </p>
             <button 
               onClick={() => onChangeTab('membership')}
               className="px-12 py-5 bg-blue-900 dark:bg-indigo-600 text-white rounded-3xl font-black text-lg hover:bg-blue-800 transition shadow-2xl shadow-blue-900/20 hover:-translate-y-1 active:scale-95"
             >
                Upgrade to Standard
             </button>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center h-96 bg-white dark:bg-slate-900 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-2xl">
             <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mb-6"></div>
             <p className="font-black text-blue-900 dark:text-indigo-400 text-[10px] tracking-[0.4em] uppercase animate-pulse">Syncing Event Manifolds...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredEvents.length === 0 ? (
                <div className="col-span-2 p-16 text-center text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-900 rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-800">
                   <p className="font-bold text-sm uppercase tracking-widest">No active protocols in this sector.</p>
                </div>
            ) : filteredEvents.map(ev => (
              <div key={ev.id} className="bg-white dark:bg-slate-900 p-10 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl hover:-translate-y-2 hover:border-indigo-500/30 transition-all duration-500 overflow-hidden relative group">
                <div className="absolute top-0 right-0 p-8 opacity-5 dark:opacity-10 text-9xl text-indigo-600 rotate-12 pointer-events-none group-hover:scale-110 transition-transform">
                   <i className={`fas ${ev.type.toLowerCase().includes('webinar') ? 'fa-laptop' : ev.location.includes('University') ? 'fa-university' : 'fa-glass-cheers'}`}></i>
                </div>
                
                <div className="relative z-10">
                    <span className={`inline-block px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest mb-6 border ${ev.location.toLowerCase().includes(profile?.school.toLowerCase() || '######') ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/50' : 'bg-blue-50 dark:bg-indigo-900/30 text-blue-700 dark:text-indigo-300 border-blue-100 dark:border-indigo-900/50'}`}>
                        {ev.type}
                    </span>
                    <h3 className="text-3xl font-black text-blue-950 dark:text-white leading-tight mb-2 pr-10 tracking-tight">{ev.title}</h3>
                    <p className="text-lg font-bold text-slate-400 dark:text-slate-500 mb-8">{ev.organizer}</p>
                    
                    <div className="flex flex-col gap-3 mb-8">
                        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300 font-bold text-xs">
                           <i className="far fa-calendar-alt text-indigo-500"></i> {ev.date}
                        </div>
                        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300 font-bold text-xs">
                           <i className="fas fa-map-marker-alt text-indigo-500"></i> {ev.location}
                        </div>
                    </div>

                    <a href={ev.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-blue-950 dark:bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-800 dark:hover:bg-indigo-500 transition shadow-lg active:scale-95">
                        Register Protocol <i className="fas fa-arrow-right text-[10px]"></i>
                    </a>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default EventsView;
